<template>
  <Teleport to="body">
    <Transition name="cookie-consent-prompt">
      <section
        v-if="props.initialConsent.choiceRequired && !settingsOpen"
        ref="promptPanel"
        class="cookie-consent-prompt"
        :class="{ 'is-footer-overlapped': footerOverlapsPrompt }"
        role="region"
        aria-label="Cookie choices"
        aria-describedby="cookie-consent-prompt-copy"
      >
        <p id="cookie-consent-prompt-copy" class="cookie-consent-prompt__copy">
          By selecting “Accept all”, you agree to optional cookies that help us understand site use and improve our marketing. View our
          <a href="/cookie-policy">Cookie Policy</a> for more information.
        </p>
        <div class="cookie-consent-prompt__actions" aria-label="Cookie choices">
          <button class="cookie-consent-prompt__button cookie-consent-prompt__button--primary" type="button" :disabled="saving" @click="saveAll">
            {{ savingAction === 'accept' ? 'Saving…' : 'Accept all' }}
          </button>
          <button ref="promptSettingsButton" class="cookie-consent-prompt__button cookie-consent-prompt__button--secondary" type="button" :disabled="saving" @click="openSettings">
            More information
          </button>
        </div>
        <p v-if="errorMessage" class="cookie-consent-error" role="alert">{{ errorMessage }}</p>
      </section>
    </Transition>

    <Transition name="cookie-consent-fade">
      <div v-if="settingsOpen" class="cookie-consent-dialog" role="presentation" @click.self="closeSettings">
        <section
          ref="dialogPanel"
          class="cookie-consent-dialog__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-settings-title"
          aria-describedby="cookie-settings-description"
          tabindex="-1"
          @keydown="handleDialogKeydown"
        >
          <button ref="closeButton" class="cookie-consent-dialog__close" type="button" aria-label="Close cookie settings" :disabled="saving" @click="closeSettings">
            <span aria-hidden="true">×</span>
          </button>

          <div class="cookie-consent-dialog__content">
            <header class="cookie-consent-dialog__header">
              <h2 id="cookie-settings-title">Privacy Preference Center</h2>
            </header>

            <p id="cookie-settings-description" class="cookie-consent-dialog__intro">
              When you visit Rooted Movement, the site may store or retrieve data in your browser. Necessary storage supports the basic functionality of the website, including security, forms, bookings, sign-in, payments, and requested program access. Optional storage may be used for analytics and marketing, such as measuring public-page visits and campaign performance. Privacy is important to us, so those optional categories stay off unless you allow them. Disabling optional categories will not prevent necessary site functions. Read our <a href="/cookie-policy">Cookie Policy</a> for full details.
            </p>

            <div class="cookie-consent-dialog__quick-actions" aria-label="Quick cookie choices">
              <button class="cookie-consent-preferences-button cookie-consent-preferences-button--secondary" type="button" :disabled="saving" @click="rejectAll">
                {{ savingAction === 'reject' ? 'Saving…' : 'Reject optional' }}
              </button>
              <button class="cookie-consent-preferences-button cookie-consent-preferences-button--primary" type="button" :disabled="saving" @click="saveAll">
                {{ savingAction === 'accept' ? 'Saving…' : 'Accept all' }}
              </button>
            </div>

            <h3 class="cookie-consent-dialog__management-title">Manage Consent Preferences by Category</h3>

            <div class="cookie-consent-categories">
              <article class="cookie-consent-category cookie-consent-category--necessary">
                <div>
                  <h3>Necessary</h3>
                  <p>Keeps the site secure and supports forms, bookings, sign-in, payments, and requested program access.</p>
                </div>
                <span class="cookie-consent-always-on" aria-label="Necessary cookies are always on">Always active</span>
              </article>

              <label class="cookie-consent-category" for="cookie-consent-analytics">
                <span>
                  <strong>Analytics</strong>
                  <small>Allows Rooted and Cloudflare to measure public-page visits and improve the website.</small>
                </span>
                <span class="cookie-consent-switch">
                  <input id="cookie-consent-analytics" v-model="draftAnalytics" type="checkbox" :disabled="saving" />
                  <span aria-hidden="true"></span>
                </span>
              </label>

              <label class="cookie-consent-category" for="cookie-consent-marketing">
                <span>
                  <strong>Marketing</strong>
                  <small>Allows Meta Pixel to measure public campaign performance and support relevant advertising.</small>
                </span>
                <span class="cookie-consent-switch">
                  <input id="cookie-consent-marketing" v-model="draftMarketing" type="checkbox" :disabled="saving" />
                  <span aria-hidden="true"></span>
                </span>
              </label>
            </div>

            <p v-if="errorMessage" class="cookie-consent-error" role="alert">{{ errorMessage }}</p>

            <footer class="cookie-consent-dialog__actions">
              <button class="cookie-consent-preferences-button cookie-consent-preferences-button--primary cookie-consent-action" type="button" :disabled="saving" @click="saveDraft">
                {{ savingAction === 'settings' ? 'Saving…' : 'Confirm preferences and close' }}
              </button>
            </footer>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { saveCookieConsent } from '../lib/cookieConsentRequest.js'

const props = defineProps({
  initialConsent: {
    type: Object,
    default: () => ({ analytics: false, marketing: false, choiceRequired: true }),
  },
  csrfToken: { type: String, required: true },
})

const CHANGE_MARKER_KEY = 'rooted-cookie-preferences-changed'
const OPEN_COOKIE_SETTINGS_EVENT = 'rooted:open-cookie-settings'
const settingsOpen = ref(false)
const footerOverlapsPrompt = ref(false)
const draftAnalytics = ref(props.initialConsent.analytics === true)
const draftMarketing = ref(props.initialConsent.marketing === true)
const saving = ref(false)
const savingAction = ref('')
const errorMessage = ref('')
const dialogPanel = ref(null)
const closeButton = ref(null)
const promptPanel = ref(null)
const promptSettingsButton = ref(null)
let previouslyFocusedElement = null
let previousBodyOverflow = ''
let footerSyncFrame = null
const initialChangeMarker = safeLocalStorage()?.getItem(CHANGE_MARKER_KEY)

const openSettings = async event => {
  previouslyFocusedElement = event?.detail?.opener || event?.currentTarget || document.activeElement
  draftAnalytics.value = props.initialConsent.analytics === true
  draftMarketing.value = props.initialConsent.marketing === true
  errorMessage.value = ''
  settingsOpen.value = true
  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'

  await nextTick()
  closeButton.value?.focus()
}

const closeSettings = () => {
  if (saving.value) return

  const focusTarget = previouslyFocusedElement
  settingsOpen.value = false
  document.body.style.overflow = previousBodyOverflow
  nextTick(() => {
    requestPromptVisibilitySync()
    window.requestAnimationFrame(() => {
      if (focusTarget?.isConnected) focusTarget.focus()
      else promptSettingsButton.value?.focus()
    })
  })
}

const saveAll = () => saveConsent(true, true, 'accept')
const rejectAll = () => saveConsent(false, false, 'reject')
const saveDraft = () => saveConsent(draftAnalytics.value, draftMarketing.value, 'settings')

const saveConsent = async (analytics, marketing, action) => {
  if (saving.value) return

  saving.value = true
  savingAction.value = action
  errorMessage.value = ''

  const result = await saveCookieConsent({ analytics, marketing }, { fallbackToken: props.csrfToken })

  if (!result.ok) {
    saving.value = false
    savingAction.value = ''
    errorMessage.value = 'We could not save your choice. Please try again.'
    return
  }

  if (props.initialConsent.marketing === true && marketing === false) {
    try {
      revokeMetaConsent()
    } catch (_error) {
      // The saved server choice remains authoritative; reload removes the runtime.
    }
  }

  try {
    safeLocalStorage()?.setItem(CHANGE_MARKER_KEY, `${Date.now()}-${Math.random().toString(36).slice(2)}`)
  } catch (_error) {
    // Cross-tab notification is best effort and must never undo a saved choice.
  }

  window.location.reload()
}

const handleDialogKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault()
    closeSettings()
    return
  }

  if (event.key !== 'Tab' || !dialogPanel.value) return

  const focusable = [...dialogPanel.value.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')]
  if (!focusable.length) return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

const syncPromptVisibility = () => {
  const footerContent = document.querySelector('#main-content > div > footer > div')
    || document.querySelector('#main-content .site-footer__inner')

  if (!footerContent || !promptPanel.value) {
    footerOverlapsPrompt.value = false
    return
  }

  const footerRect = footerContent.getBoundingClientRect()
  const promptRect = promptPanel.value.getBoundingClientRect()
  const collisionBuffer = 8
  const overlaps = footerRect.top <= promptRect.bottom + collisionBuffer
    && footerRect.bottom >= promptRect.top - collisionBuffer

  footerOverlapsPrompt.value = overlaps
  if (overlaps && promptPanel.value.contains(document.activeElement)) document.activeElement.blur()
}

const requestPromptVisibilitySync = () => {
  if (footerSyncFrame !== null) return

  footerSyncFrame = window.requestAnimationFrame(() => {
    syncPromptVisibility()
    footerSyncFrame = null
  })
}

const handleStorageChange = event => {
  if (event.key === CHANGE_MARKER_KEY && event.newValue !== event.oldValue) window.location.reload()
}
const handlePageShow = event => {
  if (!event.persisted) return
  if (safeLocalStorage()?.getItem(CHANGE_MARKER_KEY) !== initialChangeMarker) window.location.reload()
}

onMounted(() => {
  window.addEventListener('storage', handleStorageChange)
  window.addEventListener('pageshow', handlePageShow)
  window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings)
  window.addEventListener('scroll', requestPromptVisibilitySync, { passive: true })
  window.addEventListener('resize', requestPromptVisibilitySync, { passive: true })
  document.addEventListener('inertia:navigate', requestPromptVisibilitySync)
  requestPromptVisibilitySync()

  if (window.location.hash === '#cookie-settings') openSettings()
})

onBeforeUnmount(() => {
  document.body.style.overflow = previousBodyOverflow
  window.removeEventListener('storage', handleStorageChange)
  window.removeEventListener('pageshow', handlePageShow)
  window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings)
  window.removeEventListener('scroll', requestPromptVisibilitySync)
  window.removeEventListener('resize', requestPromptVisibilitySync)
  document.removeEventListener('inertia:navigate', requestPromptVisibilitySync)
  if (footerSyncFrame !== null) window.cancelAnimationFrame(footerSyncFrame)
})

function safeLocalStorage() {
  try {
    return window.localStorage
  } catch (_error) {
    return null
  }
}

function revokeMetaConsent() {
  try {
    if (typeof window.fbq === 'function') window.fbq('consent', 'revoke')
  } catch (_error) {
    // Reload still removes the provider runtime.
  }

  deleteCookie('_fbp')
  deleteCookie('_fbc')

  const storage = safeLocalStorage()
  if (!storage) return

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index)
    if (key?.startsWith('lastExternalReferrer')) storage.removeItem(key)
  }
}

function deleteCookie(name) {
  const hostname = window.location.hostname
  const domains = ['', hostname, `.${hostname}`, '.rootedmovement.co']

  domains.forEach(domain => {
    const domainAttribute = domain ? `; domain=${domain}` : ''
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${domainAttribute}`
  })
}
</script>
